/**
 * @engine/redprint — Visual Scripting Framework
 *
 * Graph-based scripting language inspired by Unreal Blueprints & Unity Bolt.
 * This is the core framework: types, graph model, IR generation, interpreter.
 *
 * @example
 * ```ts
 * import { Graph, NodeRegistry, GraphExecutor } from '@engine/redprint';
 *
 * // 1. Register nodes
 * const registry = new NodeRegistry();
 * registry.registerAll(myNodePack);
 *
 * // 2. Build a graph
 * const graph = new Graph();
 * const eventId = graph.addNode('events.update', { x: 0, y: 0 });
 * const printId = graph.addNode('debug.log', { x: 200, y: 0 });
 * graph.connect({ nodeId: eventId, portId: 'exec_out' }, { nodeId: printId, portId: 'exec_in' });
 *
 * // 3. Interpret directly
 * const executor = new GraphExecutor(graph, registry, bridge);
 * executor.initialize(entityId);
 * executor.update(dt);
 * executor.destroy();
 * ```
 */

// ---- Core types ----
export type {
  PortDataType,
  PortDirection,
  PortDefinition,
  NodeCategory,
  NodeDefinition,
  NodeInstance,
  Vec2,
  PortRef,
  Edge,
  GraphVariable,
  GraphData,
  StickyNote,
  NodeGroup,
  SubgraphPort,
  SubgraphDefinition,
  CustomTypeDefinition,
  IRInstruction,
  IRIf,
  IRFor,
  IRWhile,
  IRBreak,
  IRContinue,
  IRStatement,
  IRBlock,
  IRProgram,
  CompileDiagnostic,
  CompileResult,
  PortValue,
  ExecutionState,
  NodeCompileContext,
  NodeExecuteContext,
  NodeBehaviour,
} from './types';

export { NUMERIC_TYPES, NUMERIC_RANK, IMPLICIT_COERCIONS } from './types';

// ---- Node system ----
export { NodeRegistry, getDefaultRegistry, input, output, flowIn, flowOut } from './node/NodeRegistry';
export { canConnect, isFlowType, isNumericType, resolveNumericType, defaultValueFor, toLiteralString, defaultLiteralFor } from './node/PortTypes';
export type { ConnectionCheck } from './node/PortTypes';

// ---- Type metadata ----
export { registerTypeMeta, getTypeMeta, hasTypeMeta, getAllTypeMeta, resetTypeMeta } from './node/TypeRegistry';
export type { TypeMetadata, TypeComponent, EditorKind } from './node/TypeRegistry';

// ---- Graph model ----
export { Graph } from './graph/base';
export { GraphValidator, topologicalSortData } from './graph/RedprintValidator';
export type { ValidationResult } from './graph/base';

// ---- IR generation & optimization ----
export { IRGenerator, eventToMethod } from './compiler/IRGenerator';
export { optimizeIR } from './compiler/Optimizer';

// ---- Runtime (interpreter) ----
export { GraphExecutor } from './runtime/GraphExecutor';
export type { ExecutorState } from './runtime/GraphExecutor';
export { ExecutionContext, NullBridge } from './runtime/ExecutionContext';
export type { EngineBridge } from './runtime/ExecutionContext';

// ---- Serialization ----
export { serializeGraph, deserializeGraph, graphToJSON, graphFromJSON } from './serialization/GraphSerializer';
export type { SerializedGraph } from './serialization/GraphSerializer';

// ---- Standard node library (engine-agnostic) ----
export { STANDARD_NODES, MATH_NODES, LOGIC_NODES, FLOW_NODES, DEBUG_NODES } from './nodes';
